import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import AppModal from './AppModal';
import HelpButton from '../tours/HelpButton';

// Emoji is display only. The stored `value` is never changed by these labels.
const CATEGORIES = [
  { value: 'subscription', label: '💳 Subscription' },
  { value: 'utility',      label: '💡 Utility' },
  { value: 'insurance',    label: '🛡️ Insurance' },
  { value: 'vehicle',      label: '🚗 Vehicle' },
  { value: 'club',         label: '🎟️ Club / Membership' },
  { value: 'home',         label: '🏠 Home' },
  { value: 'document',     label: '📄 Document' },
  { value: 'health',       label: '🏥 Health' },
  { value: 'other',        label: '📦 Other' },
];

const RECURRENCES = [
  { value: 'monthly',   label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly',    label: 'Yearly' },
  { value: '2yearly',   label: 'Every 2 years' },
  { value: '3yearly',   label: 'Every 3 years' },
  { value: '5yearly',   label: 'Every 5 years' },
  { value: 'none',      label: 'One-off' },
];

function fmtDate(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function daysUntil(str) {
  if (!str) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(str);
  due.setHours(0, 0, 0, 0);
  return Math.ceil((due - today) / (24 * 60 * 60 * 1000));
}

function DueBadge({ nextDue }) {
  const days = daysUntil(nextDue);
  if (days === null) return null;
  if (days < 0)  return <span style={{ fontSize: 11, background: '#fee2e2', color: '#991b1b', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>Overdue</span>;
  if (days === 0) return <span style={{ fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>Today</span>;
  if (days <= 7)  return <span style={{ fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>In {days}d</span>;
  if (days <= 30) return <span style={{ fontSize: 11, background: '#e0f2fe', color: '#075985', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>In {days}d</span>;
  return null;
}

const BLANK_RENEWAL = { name: '', category: 'other', amount: '', currency: 'GBP', nextDue: '', recurrence: 'yearly', remindDaysBefore: 14, notes: '', householdMemberId: '' };
const BLANK_BIRTHDAY = { personName: '', birthDate: '', yearKnown: true, giftNotes: '', remindDaysBefore: 7 };

const KIND_LABEL = {
  trial_start:    'trial started',
  renewal_notice: 'renewal notice',
  price_change:   'price change',
};

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

function AZStrip({ letters, active, onChange }) {
  return (
    <div style={{ display: 'flex', overflowX: 'auto', gap: 3, marginBottom: 10, paddingBottom: 4, WebkitOverflowScrolling: 'touch' }}>
      {['All', ...ALPHABET].map(l => {
        const enabled = l === 'All' || letters.has(l);
        const isActive = active === l || (l === 'All' && !active);
        return (
          <button
            key={l}
            onClick={() => onChange(l === 'All' ? '' : l)}
            disabled={!enabled}
            style={{
              minWidth: l === 'All' ? 36 : 26, height: 26, padding: '0 4px',
              borderRadius: 6, border: 'none',
              background: isActive ? '#1a2e3b' : enabled ? '#f1f5f9' : 'transparent',
              color: isActive ? '#fff' : enabled ? '#374151' : '#d1d5db',
              fontSize: 11, fontWeight: isActive ? 700 : 500,
              cursor: enabled ? 'pointer' : 'default',
              flexShrink: 0, fontFamily: "'Raleway', sans-serif",
            }}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
}

export default function Renewals({ defaultTab = 'renewals' }) {
  const { activeWorkspaceId, workspaces } = useCurrentUser();
  const isPersonalWs = (workspaces || []).find(w => w.id === activeWorkspaceId)?.type === 'personal';

  const [tab, setTab]               = useState(defaultTab);
  const [searchRenewals, setSearchRenewals] = useState('');
  const [searchBirthdays, setSearchBirthdays] = useState('');
  // Renewals A-Z removed (search covers it); birthdays keeps its own strip.
  const [azBirthdays, setAzBirthdays] = useState('');
  const [renewals, setRenewals]     = useState([]);
  const [birthdays, setBirthdays]   = useState([]);
  const [members, setMembers]       = useState([]);
  const [memberFilter, setMemberFilter] = useState(''); // '' = all, memberId = filter
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  // Proposal state
  const [proposals, setProposals]           = useState([]);
  const [acceptingProposalId, setAcceptingProposalId] = useState(null);

  // Form state
  const [showRenewalForm, setShowRenewalForm]   = useState(false);
  const [showBdayForm, setShowBdayForm]         = useState(false);
  const [editingRenewal, setEditingRenewal]     = useState(null); // id or null
  const [editingBday, setEditingBday]           = useState(null);
  const [renewalForm, setRenewalForm]           = useState(BLANK_RENEWAL);
  const [bdayForm, setBdayForm]                 = useState(BLANK_BIRTHDAY);
  const [saving, setSaving]                     = useState(false);
  const [confirmRenewalId, setConfirmRenewalId] = useState(null);
  const [confirmBdayId,    setConfirmBdayId]    = useState(null);

  const loadProposals = useCallback(async () => {
    if (!activeWorkspaceId || !isPersonalWs) return;
    try {
      const res = await api('/api/renewal-proposals');
      if (res.ok) setProposals(await res.json());
    } catch { /* non-blocking */ }
  }, [activeWorkspaceId, isPersonalWs]);

  const load = useCallback(async () => {
    if (!activeWorkspaceId || !isPersonalWs) return;
    setLoading(true);
    setError('');
    try {
      const [rRes, bRes] = await Promise.all([
        api('/api/renewals'),
        api('/api/renewals/birthdays'),
      ]);
      if (!rRes.ok || !bRes.ok) throw new Error('Load failed');
      const [r, b] = await Promise.all([rRes.json(), bRes.json()]);
      setRenewals(r);
      setBirthdays(b);
    } catch {
      setError('Could not load renewals.');
    } finally {
      setLoading(false);
    }
  }, [activeWorkspaceId, isPersonalWs]);

  // Load household members for "For" selector and filter chips
  const loadMembers = useCallback(async () => {
    if (!activeWorkspaceId || !isPersonalWs) return;
    try {
      const res = await api('/api/household');
      if (res.ok) setMembers((await res.json()).members || []);
    } catch { /* non-blocking */ }
  }, [activeWorkspaceId, isPersonalWs]);

  useEffect(() => { load(); loadProposals(); loadMembers(); }, [load, loadProposals, loadMembers]);

  // ── Renewal CRUD ─────────────────────────────────────────────────────────────
  function openNewRenewal(prefill) {
    setEditingRenewal(null);
    setRenewalForm(prefill ? {
      ...BLANK_RENEWAL,
      name:       prefill.proposed_name || '',
      category:   prefill.proposed_category || 'subscription',
      amount:     prefill.proposed_amount != null ? String(Number(prefill.proposed_amount).toFixed(2)) : '',
      nextDue:    prefill.proposed_next_due ? new Date(prefill.proposed_next_due).toISOString().slice(0, 10) : '',
      recurrence: prefill.proposed_recurrence || (prefill.detected_kind === 'renewal_notice' ? 'yearly' : 'monthly'),
    } : BLANK_RENEWAL);
    setShowRenewalForm(true);
  }

  async function dismissProposal(id) {
    try {
      await api(`/api/renewal-proposals/${id}/dismiss`, { method: 'POST' });
      setProposals(prev => prev.filter(p => p.id !== id));
    } catch { /* best effort */ }
  }

  function openTrackProposal(proposal) {
    setAcceptingProposalId(proposal.id);
    openNewRenewal(proposal);
  }

  function openEditRenewal(item) {
    setEditingRenewal(item.id);
    setRenewalForm({
      name:               item.name,
      category:           item.category,
      amount:             item.amount != null ? String(item.amount) : '',
      currency:           item.currency || 'GBP',
      nextDue:            item.nextDue ? item.nextDue.slice(0, 10) : '',
      recurrence:         item.recurrence,
      remindDaysBefore:   item.remindDaysBefore,
      notes:              item.notes || '',
      householdMemberId:  item.householdMemberId || '',
    });
    setShowRenewalForm(true);
  }

  async function submitRenewal(e) {
    e.preventDefault();
    if (saving) return;
    const remindDays = Number(renewalForm.remindDaysBefore);
    if (!Number.isInteger(remindDays) || remindDays < 1 || remindDays > 365) {
      setError('Remind me must be a whole number of days between 1 and 365.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body = {
        name:               renewalForm.name.trim(),
        category:           renewalForm.category,
        amount:             renewalForm.amount !== '' ? Number(renewalForm.amount) : null,
        currency:           renewalForm.currency,
        nextDue:            renewalForm.nextDue,
        recurrence:         renewalForm.recurrence,
        remindDaysBefore:   Number(renewalForm.remindDaysBefore),
        notes:              renewalForm.notes.trim() || null,
        householdMemberId:  renewalForm.householdMemberId || null,
      };
      const res = editingRenewal
        ? await api(`/api/renewals/${editingRenewal}`, { method: 'PATCH', body: JSON.stringify(body) })
        : await api('/api/renewals',                   { method: 'POST',  body: JSON.stringify(body) });
      if (!res.ok) { const d = await res.json(); setError(d.error || 'Failed to save.'); return; }
      // If this save was triggered from a proposal, mark it accepted
      if (acceptingProposalId) {
        await api(`/api/renewal-proposals/${acceptingProposalId}/accept`, { method: 'POST' }).catch(() => {});
        setProposals(prev => prev.filter(p => p.id !== acceptingProposalId));
        setAcceptingProposalId(null);
      }
      setShowRenewalForm(false);
      await load();
    } catch {
      setError('Failed to save renewal.');
    } finally {
      setSaving(false);
    }
  }

  async function deleteRenewal(id) {
    try {
      await api(`/api/renewals/${id}`, { method: 'DELETE' });
      setRenewals(prev => prev.filter(r => r.id !== id));
    } catch {
      setError('Failed to delete.');
    }
  }

  // ── Birthday CRUD ─────────────────────────────────────────────────────────────
  function openNewBday() {
    setEditingBday(null);
    setBdayForm(BLANK_BIRTHDAY);
    setShowBdayForm(true);
  }

  function openEditBday(item) {
    setEditingBday(item.id);
    setBdayForm({
      personName:       item.personName,
      birthDate:        item.birthDate ? item.birthDate.slice(0, 10) : '',
      yearKnown:        item.yearKnown,
      giftNotes:        item.giftNotes || '',
      remindDaysBefore: item.remindDaysBefore,
    });
    setShowBdayForm(true);
  }

  async function submitBday(e) {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      const body = {
        personName:       bdayForm.personName.trim(),
        birthDate:        bdayForm.birthDate,
        yearKnown:        bdayForm.yearKnown,
        giftNotes:        bdayForm.giftNotes.trim() || null,
        remindDaysBefore: Number(bdayForm.remindDaysBefore),
      };
      const res = editingBday
        ? await api(`/api/renewals/birthdays/${editingBday}`, { method: 'PATCH', body: JSON.stringify(body) })
        : await api('/api/renewals/birthdays',                { method: 'POST',  body: JSON.stringify(body) });
      if (!res.ok) { const d = await res.json(); setError(d.error || 'Failed to save.'); return; }
      setShowBdayForm(false);
      await load();
    } catch {
      setError('Failed to save birthday.');
    } finally {
      setSaving(false);
    }
  }

  async function deleteBday(id) {
    try {
      await api(`/api/renewals/birthdays/${id}`, { method: 'DELETE' });
      setBirthdays(prev => prev.filter(b => b.id !== id));
    } catch {
      setError('Failed to delete.');
    }
  }

  // ── Group renewals by category (with optional member filter + A-Z) ────────────
  const memberName = (id) => members.find(m => m.id === id)?.name || null;

  // Pre-filter for member + search (used for available-letters computation)
  const renewalsPreFilter = renewals
    .filter(r => !memberFilter || r.householdMemberId === memberFilter)
    .filter(r => !searchRenewals || r.name.toLowerCase().includes(searchRenewals.toLowerCase()));
  const birthdaysPreFilter = searchBirthdays
    ? birthdays.filter(b => b.personName.toLowerCase().includes(searchBirthdays.toLowerCase()))
    : birthdays;

  const birthdayLetters  = new Set(birthdaysPreFilter.map(b => b.personName.trim()[0]?.toUpperCase()).filter(Boolean));

  const filteredRenewals = renewalsPreFilter;
  const filteredBirthdays = azBirthdays
    ? birthdaysPreFilter.filter(b => b.personName.trim()[0]?.toUpperCase() === azBirthdays)
    : birthdaysPreFilter;
  const grouped = filteredRenewals.reduce((acc, r) => {
    const key = r.category || 'other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});
  const categoryOrder = CATEGORIES.map(c => c.value);
  const sortedGroups = Object.keys(grouped).sort(
    (a, b) => (categoryOrder.indexOf(a) + 1 || 99) - (categoryOrder.indexOf(b) + 1 || 99)
  );

  const catLabel = v => CATEGORIES.find(c => c.value === v)?.label || v;

  // ── Birthday: project to next occurrence ─────────────────────────────────────
  function nextBirthdayDate(birthDate) {
    if (!birthDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const bd = new Date(birthDate);
    let next = new Date(today.getFullYear(), bd.getMonth(), bd.getDate());
    if (next < today) next = new Date(today.getFullYear() + 1, bd.getMonth(), bd.getDate());
    return next.toISOString().slice(0, 10);
  }

  const inputStyle = {
    width: '100%', padding: '8px 10px', borderRadius: 8,
    border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box',
    outline: 'none',
  };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 3 };
  const fieldStyle = { marginBottom: 12 };

  // Section styling reproduced inline from components/TaskForm.css so the renewals
  // form reads like the same designer made TaskForm. Numbers copied verbatim:
  //   .form-section        -> paddingBottom 1.1rem
  //   .form-section+.form-section -> paddingTop 1.1rem; borderTop 1px solid #f0f2f5
  //   .form-section-label  -> Poppins 0.75rem/700, #b0b8be, uppercase, 0.09em, mb 0.85rem
  //   .form-row-2          -> grid 1fr 1fr, gap 0.75rem, mb 0.85rem
  //   .task-form .form-group -> mb 0.55rem
  const sectionStyle       = { paddingBottom: '1.1rem' };
  const sectionDividerStyle = { paddingTop: '1.1rem', borderTop: '1px solid #f0f2f5' };
  const sectionLabelStyle  = { fontFamily: "'Poppins', sans-serif", fontSize: '0.75rem', fontWeight: 700, color: '#b0b8be', textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: '0.85rem' };
  const row2Style          = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.85rem' };
  const groupStyle         = { marginBottom: '0.55rem' };
  const sumLabelStyle      = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8', marginBottom: 4 };

  // ── Summary metrics (display only, from existing data; never mutate stored values) ──
  const curSym = (c) => (c === 'GBP' ? '£' : c === 'EUR' ? '€' : c === 'USD' ? '$' : (c || '') + ' ');
  // Annualisation factor per recurrence. One-off ('none') and any unknown value are
  // excluded entirely (undefined -> not counted).
  const ANNUAL_FACTOR = { monthly: 12, quarterly: 4, yearly: 1, '2yearly': 1 / 2, '3yearly': 1 / 3, '5yearly': 1 / 5 };

  // Contributors to the annual total: an amount AND an annualisable recurrence.
  const annualContribs = renewals.filter(r => r.amount != null && ANNUAL_FACTOR[r.recurrence] != null);
  const noAmountCount  = renewals.filter(r => r.amount == null).length;
  // Sum per currency. We NEVER add across currencies.
  const annualByCurrency = annualContribs.reduce((acc, r) => {
    const cur = r.currency || 'GBP';
    acc[cur] = (acc[cur] || 0) + Number(r.amount) * ANNUAL_FACTOR[r.recurrence];
    return acc;
  }, {});
  const annualCurrencies = Object.keys(annualByCurrency);
  const annualParts = annualCurrencies.map(c => `${curSym(c)}${annualByCurrency[c].toFixed(2)}/yr`);

  // Exclusions the card must acknowledge so no renewal vanishes silently. These
  // three buckets partition every renewal (no double counting): annualContribs
  // (amount + recurring), noAmount (amount == null), oneOff (amount + one-off).
  const oneOffCount = renewals.filter(r => r.amount != null && r.recurrence === 'none').length;
  const exclParts = [];
  if (noAmountCount > 0) exclParts.push(`${noAmountCount} excluded (no amount)`);
  if (oneOffCount > 0)   exclParts.push(`${oneOffCount} one-off`);
  const exclSuffix = exclParts.length ? ', ' + exclParts.join(', ') : '';
  // When there is no annual total to show, still say why in plain terms.
  let noTotalMsg;
  if (noAmountCount > 0 && oneOffCount === 0)      noTotalMsg = 'No amounts added yet, so there is nothing to total.';
  else if (oneOffCount > 0 && noAmountCount === 0) noTotalMsg = 'Only one-off costs, so there is nothing to total annually.';
  else if (noAmountCount > 0 && oneOffCount > 0)   noTotalMsg = `No amounts to total yet, ${exclParts.join(', ')}.`;
  else                                             noTotalMsg = 'No recurring cost tracked.';

  // Next renewal due: soonest upcoming; if none upcoming, the most recent overdue.
  const datedRenewals = renewals
    .filter(r => r.nextDue)
    .map(r => ({ r, d: daysUntil(r.nextDue) }))
    .filter(x => x.d !== null);
  const upcomingSorted = datedRenewals.filter(x => x.d >= 0).sort((a, b) => a.d - b.d);
  const overdueSorted  = datedRenewals.filter(x => x.d < 0).sort((a, b) => b.d - a.d); // closest to today first
  const nextDueItem = upcomingSorted[0] || overdueSorted[0] || null;
  const nextDueWhen = nextDueItem
    ? (nextDueItem.d < 0 ? `${Math.abs(nextDueItem.d)} day${Math.abs(nextDueItem.d) === 1 ? '' : 's'} overdue`
       : nextDueItem.d === 0 ? 'due today'
       : `in ${nextDueItem.d} day${nextDueItem.d === 1 ? '' : 's'}`)
    : null;

  // Due within 30 days (0..30 inclusive).
  const dueSoonCount = datedRenewals.filter(x => x.d >= 0 && x.d <= 30).length;

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '1rem 1rem 4rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 1rem' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a2e3b', margin: 0 }}>
          {tab === 'birthdays' ? 'Birthdays & Anniversaries' : 'Renewals'}
        </h2>
        {tab === 'birthdays' ? <HelpButton tourId="birthdays" /> : <HelpButton tourId="renewals" />}
      </div>

      {error && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</p>}
      {!activeWorkspaceId && <p style={{ color: '#bbb', fontSize: 13 }}>Connecting to workspace…</p>}
      {activeWorkspaceId && loading && <p style={{ color: '#aaa', fontSize: 13 }}>Loading…</p>}

      {/* ── Renewals tab ── */}
      {tab === 'renewals' && !loading && (
        <>
          {proposals.length > 0 && (
            <div style={{ marginBottom: 20, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#92400e', marginBottom: 8 }}>
                Aidey spotted in your emails
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {proposals.map(p => {
                  const dateStr = p.proposed_next_due
                    ? ' ' + new Date(p.proposed_next_due).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                    : '';
                  const amtStr = p.proposed_amount != null
                    ? ' — £' + Number(p.proposed_amount).toFixed(2)
                    : '';
                  return (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, color: '#78350f' }}>
                        <strong>{p.proposed_name}</strong>
                        {' '}{KIND_LABEL[p.detected_kind] || p.detected_kind}{dateStr}{amtStr}
                      </span>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button
                          onClick={() => openTrackProposal(p)}
                          style={{ fontSize: 12, padding: '4px 12px', borderRadius: 6, border: '1px solid #d97706', background: '#fffbeb', color: '#92400e', cursor: 'pointer', fontWeight: 600 }}
                        >Track it</button>
                        <button
                          onClick={() => dismissProposal(p.id)}
                          style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#9ca3af', cursor: 'pointer' }}
                        >Dismiss</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Summary card: overview computed from existing data only */}
          {renewals.length > 0 && (
            <div style={{ marginBottom: 20, background: '#f8fafc', border: '1px solid #e5e9ee', borderRadius: 12, padding: '14px 16px', display: 'flex', flexWrap: 'wrap', gap: '14px 24px' }}>
              {nextDueItem && (
                <div style={{ minWidth: 0 }}>
                  <div style={sumLabelStyle}>Next due</div>
                  <div style={{ fontSize: 14, color: '#1a2e3b', fontWeight: 600, overflowWrap: 'anywhere' }}>{nextDueItem.r.name}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{nextDueWhen}</div>
                </div>
              )}
              <div style={{ minWidth: 0 }}>
                <div style={sumLabelStyle}>Annual cost</div>
                {annualParts.length > 0 ? (
                  <>
                    <div style={{ fontSize: 14, color: '#1a2e3b', fontWeight: 600, overflowWrap: 'anywhere' }}>{annualParts.join(' and ')}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      across {annualContribs.length} renewal{annualContribs.length === 1 ? '' : 's'}{exclSuffix}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: '#64748b' }}>{noTotalMsg}</div>
                )}
              </div>
              {dueSoonCount > 0 && (
                <div style={{ minWidth: 0 }}>
                  <div style={sumLabelStyle}>Due soon</div>
                  <div style={{ fontSize: 14, color: '#1a2e3b', fontWeight: 600 }}>{dueSoonCount}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>within 30 days</div>
                </div>
              )}
            </div>
          )}

          {renewals.length === 0 ? (
            /* First run: no filters, one line plus the Add button */
            <div>
              <p style={{ color: '#64748b', fontSize: 14, marginBottom: 14 }}>You have no renewals yet. Add your first one to start tracking costs and reminders.</p>
              <button onClick={() => openNewRenewal()} style={{
                padding: '8px 18px', borderRadius: 8, background: '#1a2e3b',
                color: '#fff', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}>+ Add renewal</button>
            </div>
          ) : (
            <>
              {/* Member filter chips */}
              {members.length > 1 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                  {[{ id: '', name: 'All' }, ...members].map(m => (
                    <button
                      key={m.id}
                      onClick={() => setMemberFilter(m.id)}
                      style={{
                        padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        border: '1px solid', cursor: 'pointer',
                        borderColor: memberFilter === m.id ? '#1a2e3b' : '#d1d5db',
                        background:  memberFilter === m.id ? '#1a2e3b' : '#fff',
                        color:       memberFilter === m.id ? '#fff'    : '#555',
                      }}
                    >
                      {m.name}
                    </button>
                  ))}
                </div>
              )}

              <input
                type="search"
                placeholder="Search renewals…"
                value={searchRenewals}
                onChange={e => setSearchRenewals(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, marginBottom: 14, boxSizing: 'border-box' }}
              />

              <button onClick={() => openNewRenewal()} style={{
                marginBottom: 20, padding: '8px 18px', borderRadius: 8,
                background: '#1a2e3b', color: '#fff', border: 'none',
                fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}>+ Add renewal</button>

              {filteredRenewals.length === 0 && (
                <p style={{ color: '#94a3b8', fontSize: 14 }}>No renewals match your search or filter.</p>
              )}
            </>
          )}

          {sortedGroups.map(cat => (
            <div key={cat} style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#aaa', margin: '0 0 8px' }}>
                {catLabel(cat)}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {grouped[cat].map(item => (
                  <div key={item.id} style={{
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                    padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: 14, color: '#1a2e3b' }}>{item.name}</span>
                        <DueBadge nextDue={item.nextDue} />
                        {item.householdMemberId && memberName(item.householdMemberId) && (
                          <span style={{ fontSize: 11, background: '#ede9fe', color: '#6d28d9', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>
                            {memberName(item.householdMemberId)}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>
                        Due {fmtDate(item.nextDue)}
                        {item.amount != null && (
                          <span> &middot; {item.currency === 'GBP' ? '£' : item.currency === 'EUR' ? '€' : '$'}{Number(item.amount).toFixed(2)}/{RECURRENCES.find(r => r.value === item.recurrence)?.label?.toLowerCase() || item.recurrence}</span>
                        )}
                        <span> &middot; {RECURRENCES.find(r => r.value === item.recurrence)?.label || item.recurrence}</span>
                      </div>
                      {item.notes && <div style={{ fontSize: 12, color: '#999', marginTop: 2, fontStyle: 'italic' }}>{item.notes}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button onClick={() => openEditRenewal(item)} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', color: '#555' }}>Edit</button>
                      {confirmRenewalId === item.id ? (
                        <>
                          <span style={{ fontSize: 12, color: '#dc2626', alignSelf: 'center' }}>Delete?</span>
                          <button onClick={() => { deleteRenewal(item.id); setConfirmRenewalId(null); }} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff7f7', cursor: 'pointer', color: '#dc2626', fontWeight: 700 }}>Yes</button>
                          <button onClick={() => setConfirmRenewalId(null)} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', color: '#555' }}>No</button>
                        </>
                      ) : (
                        <button onClick={() => setConfirmRenewalId(item.id)} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', cursor: 'pointer', color: '#dc2626' }}>Delete</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Renewal form */}
          <AppModal open={showRenewalForm} onClose={() => { setShowRenewalForm(false); setAcceptingProposalId(null); }} ariaLabel={editingRenewal ? 'Edit renewal' : 'Add renewal'} maxWidth={440}>
              <form onSubmit={submitRenewal}>
                <h3 style={{ margin: '0 0 1rem', fontSize: 16, fontWeight: 700 }}>{editingRenewal ? 'Edit renewal' : 'Add renewal'}</h3>
                {/* Section 1: What is it */}
                <div style={sectionStyle}>
                  <div style={sectionLabelStyle}>What is it</div>
                  <div style={groupStyle}>
                    <label style={labelStyle}>Name</label>
                    <input required style={inputStyle} value={renewalForm.name} onChange={e => setRenewalForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Broadband, Car insurance" />
                  </div>
                  <div style={{ ...row2Style, marginBottom: 0 }}>
                    <div>
                      <label style={labelStyle}>Category</label>
                      <select style={{ ...inputStyle }} value={renewalForm.category} onChange={e => setRenewalForm(f => ({ ...f, category: e.target.value }))}>
                        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Recurrence</label>
                      <select style={{ ...inputStyle }} value={renewalForm.recurrence} onChange={e => setRenewalForm(f => ({ ...f, recurrence: e.target.value }))}>
                        {RECURRENCES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Section 2: What it costs */}
                <div style={{ ...sectionStyle, ...sectionDividerStyle }}>
                  <div style={sectionLabelStyle}>What it costs</div>
                  <div style={{ ...row2Style, gridTemplateColumns: '1fr 90px', marginBottom: 0 }}>
                    <div>
                      <label style={labelStyle}>Amount (optional)</label>
                      <input style={inputStyle} type="number" step="0.01" min="0" value={renewalForm.amount} onChange={e => setRenewalForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
                    </div>
                    <div>
                      <label style={labelStyle}>Currency</label>
                      <select style={{ ...inputStyle }} value={renewalForm.currency} onChange={e => setRenewalForm(f => ({ ...f, currency: e.target.value }))}>
                        <option value="GBP">GBP</option>
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Section 3: When to tell me */}
                <div style={{ ...sectionStyle, ...sectionDividerStyle }}>
                  <div style={sectionLabelStyle}>When to tell me</div>
                  <div style={{ ...row2Style, marginBottom: members.length > 0 ? '0.85rem' : 0 }}>
                    <div>
                      <label style={labelStyle}>Next due date</label>
                      <input required style={inputStyle} type="date" value={renewalForm.nextDue} onChange={e => setRenewalForm(f => ({ ...f, nextDue: e.target.value }))} />
                    </div>
                    <div>
                      <label style={labelStyle}>Remind me (days before)</label>
                      <input style={inputStyle} type="number" min="1" max="365" value={renewalForm.remindDaysBefore} onChange={e => setRenewalForm(f => ({ ...f, remindDaysBefore: e.target.value }))} />
                      {(() => {
                        const n = Number(renewalForm.remindDaysBefore);
                        const bad = renewalForm.remindDaysBefore !== '' && (!Number.isInteger(n) || n < 1 || n > 365);
                        return bad ? <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>Enter a whole number of days between 1 and 365.</div> : null;
                      })()}
                    </div>
                  </div>
                  {members.length > 0 && (
                    <div style={groupStyle}>
                      <label style={labelStyle}>For (optional)</label>
                      <select style={{ ...inputStyle }} value={renewalForm.householdMemberId} onChange={e => setRenewalForm(f => ({ ...f, householdMemberId: e.target.value }))}>
                        <option value="">Household (no specific person)</option>
                        {members.map(m => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Notes stays last */}
                <div style={fieldStyle}>
                  <label style={labelStyle}>Notes (optional)</label>
                  <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} value={renewalForm.notes} onChange={e => setRenewalForm(f => ({ ...f, notes: e.target.value }))} placeholder="Account number, reference, etc." />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => { setShowRenewalForm(false); setAcceptingProposalId(null); }} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                  <button type="submit" disabled={saving} style={{ padding: '8px 18px', borderRadius: 8, background: '#1a2e3b', color: '#fff', border: 'none', fontWeight: 600, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>
                </div>
              </form>
          </AppModal>
          {/* end renewal form */}
        </>
      )}

      {/* ── Birthdays tab ── */}
      {tab === 'birthdays' && !loading && (
        <>
          <AZStrip letters={birthdayLetters} active={azBirthdays} onChange={setAzBirthdays} />

          <input
            type="search"
            placeholder="Search birthdays…"
            value={searchBirthdays}
            onChange={e => setSearchBirthdays(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, marginBottom: 14, boxSizing: 'border-box' }}
          />

          <button onClick={openNewBday} style={{
            marginBottom: 20, padding: '8px 18px', borderRadius: 8,
            background: '#1a2e3b', color: '#fff', border: 'none',
            fontWeight: 600, fontSize: 14, cursor: 'pointer',
          }}>+ Add birthday</button>

          {birthdays.length === 0 && (
            <p style={{ color: '#aaa', fontSize: 14 }}>No birthdays yet. Add one above.</p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filteredBirthdays.map(item => {
              const next = nextBirthdayDate(item.birthDate);
              return (
                <div key={item.id} style={{
                  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                  padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: 14, color: '#1a2e3b' }}>{item.personName}</span>
                      <DueBadge nextDue={next} />
                    </div>
                    <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>
                      {item.yearKnown && item.birthDate
                        ? new Date(item.birthDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                        : item.birthDate ? new Date(item.birthDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }) : ''}
                      {next && <span> &middot; Next: {fmtDate(next)}</span>}
                    </div>
                    {item.giftNotes && <div style={{ fontSize: 12, color: '#999', marginTop: 2, fontStyle: 'italic' }}>{item.giftNotes}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => openEditBday(item)} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', color: '#555' }}>Edit</button>
                    {confirmBdayId === item.id ? (
                      <>
                        <span style={{ fontSize: 12, color: '#dc2626', alignSelf: 'center' }}>Delete?</span>
                        <button onClick={() => { deleteBday(item.id); setConfirmBdayId(null); }} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff7f7', cursor: 'pointer', color: '#dc2626', fontWeight: 700 }}>Yes</button>
                        <button onClick={() => setConfirmBdayId(null)} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', color: '#555' }}>No</button>
                      </>
                    ) : (
                      <button onClick={() => setConfirmBdayId(item.id)} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', cursor: 'pointer', color: '#dc2626' }}>Delete</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Birthday form */}
          <AppModal open={showBdayForm} onClose={() => setShowBdayForm(false)} ariaLabel={editingBday ? 'Edit birthday' : 'Add birthday'} maxWidth={400}>
              <form onSubmit={submitBday}>
                <h3 style={{ margin: '0 0 1rem', fontSize: 16, fontWeight: 700 }}>{editingBday ? 'Edit birthday' : 'Add birthday'}</h3>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Name</label>
                  <input required style={inputStyle} value={bdayForm.personName} onChange={e => setBdayForm(f => ({ ...f, personName: e.target.value }))} placeholder="e.g. Mum, James" />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Date of birth</label>
                  <input required style={inputStyle} type="date" value={bdayForm.birthDate} onChange={e => setBdayForm(f => ({ ...f, birthDate: e.target.value }))} />
                </div>
                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" id="yearKnown" checked={bdayForm.yearKnown} onChange={e => setBdayForm(f => ({ ...f, yearKnown: e.target.checked }))} />
                  <label htmlFor="yearKnown" style={{ fontSize: 13, color: '#555' }}>Year is known</label>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Gift notes (optional)</label>
                  <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 56 }} value={bdayForm.giftNotes} onChange={e => setBdayForm(f => ({ ...f, giftNotes: e.target.value }))} placeholder="Ideas, sizes, wishes…" />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Remind me (days before)</label>
                  <input style={inputStyle} type="number" min="1" max="90" value={bdayForm.remindDaysBefore} onChange={e => setBdayForm(f => ({ ...f, remindDaysBefore: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setShowBdayForm(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                  <button type="submit" disabled={saving} style={{ padding: '8px 18px', borderRadius: 8, background: '#1a2e3b', color: '#fff', border: 'none', fontWeight: 600, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>
                </div>
              </form>
          </AppModal>
        </>
      )}
    </div>
  );
}
